/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

import {defaultOptions, ImageResizeOptions} from './imageresizeoptions';
import {Resize} from './modules/Resize';
import Quill, {Parchment} from "quill";
import {hash} from "../../types";
import {defaultsDeep} from "lodash-es";
import {BaseModule} from "./modules/BaseModule";
import Module from "quill/core/module";
import {DisplaySize} from "./modules/DisplaySize";
import {Toolbar} from "./modules/Toolbar";

/**
 * Overgenomen van quill-image-resize-alt-module@4.0.0 met aanpassingen voor typescript en quill2
 * Jelmer Jellema - Spin in het Web - 5-2024
 */
const knownModules = {Resize, DisplaySize, Toolbar};

export default class ImageResize extends Module<ImageResizeOptions> {
  moduleClasses: (string | typeof BaseModule)[];
  modules: BaseModule[];
  img: HTMLImageElement | null = null;
  overlay: HTMLDivElement | null = null;

  constructor(quill: Quill, options: hash<any> = {}) {
    super(quill, options);

    let moduleClasses: (string | typeof BaseModule)[] = [];
    if (options['modules']) {
      moduleClasses = options['modules'].slice();
    }

    this.options = defaultsDeep({}, options, defaultOptions);

    if (moduleClasses.length > 0) {
      this.options.modules = moduleClasses;
    }

    this.quill.root.addEventListener('click', this.handleClick, false);
    if (this.quill.root.parentElement) {
      this.quill.root.parentElement.style.position = this.quill.root.parentElement.style.position || 'relative';
    }

    this.moduleClasses = this.options.modules || [];
    this.modules = [];
  }

  initializeModules() {
    this.removeModules();
    this.modules = this.moduleClasses.map(
      ModuleClass => new (typeof (ModuleClass) === 'string' ? (knownModules as any)[ModuleClass] : ModuleClass)(this, this.quill, this.options),
    );
    this.modules.forEach((module) => {
      module.onCreate();
    });
    this.onUpdate();
  };

  onUpdate() {
    this.repositionElements();
    this.modules.forEach((module) => {
      module.onUpdate();
    });
  };

  removeModules() {
    this.modules.forEach((module) => {
      module.onDestroy();
    });
    this.modules = [];
  };

  handleClick = (evt: MouseEvent) => {
    if ((<HTMLElement>evt?.target)?.tagName.toUpperCase() === 'IMG') {
      if (this.img === evt.target) {
        return;
      }
      if (this.img) {
        this.hide();
      }
      this.show(<HTMLImageElement>evt.target);
    } else if (this.img) {
      this.hide();
    }
  };

  show(img: HTMLImageElement) {
    this.img = img;
    this.showOverlay();
    this.initializeModules();
  };

  showOverlay() {
    if (this.overlay) {
      this.hideOverlay();
    }
    this.quill.setSelection(null);
    this.setUserSelect('none');
    document.addEventListener('keyup', this.checkImage, true);
    this.quill.root.addEventListener('input', this.checkImage as any, true);
    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, this.options.overlayStyles);
    if (this.quill.root.parentNode) {
      this.quill.root.parentNode.appendChild(this.overlay);
    }
    this.repositionElements();
  };

  hideOverlay() {
    if (!this.overlay) {
      return;
    }
    if (this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    this.overlay = null;
    document.removeEventListener('keyup', this.checkImage);
    this.quill.root.removeEventListener('input', this.checkImage as any);
    this.setUserSelect('');
  };

  repositionElements() {
    if (!this.overlay || !this.img) {
      return;
    }
    const parent = this.quill.root.parentElement;
    if (!parent) return;
    const imgRect = this.img.getBoundingClientRect();
    const containerRect = parent.getBoundingClientRect();
    Object.assign(this.overlay.style, {
      left: `${imgRect.left - containerRect.left - 1 + parent.scrollLeft}px`,
      top: `${imgRect.top - containerRect.top + parent.scrollTop}px`,
      width: `${imgRect.width}px`,
      height: `${imgRect.height}px`,
    });
  };

  hide() {
    this.hideOverlay();
    this.removeModules();
    this.img = null;
  };

  setUserSelect = (value: string) => {
    const props = [
      'userSelect',
      'mozUserSelect',
      'webkitUserSelect',
      'msUserSelect',
    ];
    props.forEach((prop) => {
      (this.quill.root.style as any)[prop] = value;
      (document.documentElement.style as any)[prop] = value;
    });
  };

  checkImage = (evt: KeyboardEvent) => {
    if (this.img) {
      if (['Delete', 'Del', 'Backspace'].includes(evt.key)) {
        (<Parchment.Blot>Quill.find(this.img)).deleteAt(0, 1);
      }
      this.hide();
    }
  };
}
